import { useEffect, useRef, useState } from 'react';

type Callback = () => void;
type EffectWithDestructor = () => Callback;

// like useEffect but also triggers on tab switch (not just mount/unmount)
export function useVisibilityEffect(
  effect: EffectWithDestructor,
  deps: Array<any>,
): void {
  const mounted = useRef<boolean>(false);
  const visible = useRef<boolean>(document.visibilityState === 'visible');

  // state to trigger re-render when needed
  const [, setShowing] = useState<boolean>(false);
  const lastDestructor = useRef<() => void>(() => ({}));

  const handleVisibilityChange = () => {
    const lastVisible = visible.current;
    visible.current = document.visibilityState === 'visible';

    if (lastVisible !== visible.current) {
      if (visible.current && mounted.current) {
        lastDestructor.current = effect();
        setShowing(true);
      }
      if (!visible.current && mounted.current) {
        lastDestructor.current();
        lastDestructor.current = () => ({});
        setShowing(false);
      }
    }
  };

  useEffect(() => {
    mounted.current = true;
    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (visible.current) {
      lastDestructor.current = effect();
      setShowing(true);
    }

    return () => {
      mounted.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (visible.current) {
        lastDestructor.current();
        lastDestructor.current = () => ({});
        setShowing(false);
      }
    };
  }, [...deps]);
}
